import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, setAuthToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GaugeCircle } from "lucide-react";

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/invitations/${token}`);
        if (mounted) {
          setInv(data);
          setFullName(data.name || "");
        }
      } catch (e) {
        if (mounted) setErr(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auth/invitations/${token}/accept`, { token, password, full_name: fullName });
      setAuthToken(data.access_token);
      localStorage.setItem("eb_refresh_token", data.refresh_token);
      await refresh();
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col justify-center items-center p-5" data-testid="accept-invite-page">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="w-9 h-9 rounded-lg bg-navy-900 flex items-center justify-center">
          <GaugeCircle className="w-5 h-5 text-orange-500" />
        </div>
        <span className="font-bold text-lg">EntryBook</span>
      </Link>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-card border border-navy-100 p-8">
        {loading ? (
          <p className="text-navy-600">Loading…</p>
        ) : inv ? (
          <>
            <div className="eb-overline mb-2">Accept invitation</div>
            <h1 className="text-2xl font-semibold mb-1" data-testid="invite-org-name">Join {inv.org_name}</h1>
            <p className="text-sm text-navy-600 mb-5">
              You've been invited as <strong className="text-navy-900">{inv.role.replace("_", " ")}</strong> · {inv.email}
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="eb-label">Your name</label>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="eb-input" data-testid="invite-name-input" />
              </div>
              <div>
                <label className="eb-label">Password</label>
                <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="eb-input" data-testid="invite-password-input" />
              </div>
              <div>
                <label className="eb-label">Confirm password</label>
                <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="eb-input" data-testid="invite-confirm-input" />
              </div>
              {err && <div className="rounded-md bg-danger/10 border border-danger/30 text-danger text-sm px-3 py-2" data-testid="invite-error">{err}</div>}
              <button type="submit" disabled={submitting} className="btn-accent w-full" data-testid="invite-submit-btn">
                {submitting ? "Creating…" : "Accept & Create account"}
              </button>
            </form>
          </>
        ) : (
          <div>
            <h1 className="text-2xl font-semibold mb-3">Invitation invalid</h1>
            <p className="text-sm text-navy-600">{err || "This invitation could not be loaded."}</p>
            <Link to="/login" className="btn-outline inline-flex mt-5">Go to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
